import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Compass, Crown, Trophy, Check, Clock, Play } from '@phosphor-icons/react';
import { DEVOTIONAL_IMAGES } from '../config/devotionalImages';

const API = process.env.REACT_APP_BACKEND_URL;

// Category colors for visual distinction
const CATEGORY_COLORS = {
  'Leadership': '#3B82F6',
  'Vision': '#8B5CF6', 
  'Resilience': '#10B981',
  'Purpose': '#F59E0B',
  'Strategy': '#EF4444',
  'Legacy': '#C4A661'
};

const DevotionalsPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [devotionals, setDevotionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDevotionals();
    if (user) {
      fetchStats();
    }
  }, [filter, categoryFilter, user]);

  const fetchDevotionals = async () => {
    try {
      let url = `${API}/api/devotionals`;
      const params = new URLSearchParams();
      
      if (filter === 'old') params.append('testament', 'Old Testament');
      if (filter === 'new') params.append('testament', 'New Testament');
      if (categoryFilter) params.append('category', categoryFilter);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const headers = user ? getAuthHeader() : {};
      const res = await axios.get(url, { headers });
      setDevotionals(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/devotionals/stats`, {
        headers: getAuthHeader()
      });
      setStats(res.data);
    } catch (error) {
    }
  };

  const featuredDevotional = devotionals[0];
  const categories = ['Leadership', 'Vision', 'Resilience', 'Purpose', 'Strategy', 'Legacy'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center">
        <div className="animate-pulse text-[#6B6560] font-satoshi">Loading devotionals...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EE] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#F5F3EE]/95 backdrop-blur-sm border-b border-[#1A1714]/10">
        <div className="container mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-instrument text-2xl text-[#1A1714]">POWR Devotional</h1>
              <p className="text-sm text-[#6B6560] font-satoshi">Walk in power. Lead with purpose.</p>
            </div>
            <Link to="/community" className="text-[#6B6560] hover:text-[#1A1714] transition-colors">
              <span className="text-sm font-satoshi">Back to Community</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Featured Devotional Hero */}
      {featuredDevotional && (
        <section className="px-5 py-6">
          <p className="text-xs font-satoshi font-medium text-[#6B6560] uppercase tracking-wider mb-3">
            Today's Devotional
          </p>
          <Link to={`/devotional/${featuredDevotional.id}`}>
            <motion.div 
              className="relative h-[280px] rounded-3xl overflow-hidden group cursor-pointer"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ 
                  backgroundImage: `url(${DEVOTIONAL_IMAGES[featuredDevotional.character_image] || DEVOTIONAL_IMAGES.david})`,
                  backgroundColor: '#2A2724'
                }}
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-satoshi font-medium text-white"
                    style={{ backgroundColor: CATEGORY_COLORS[featuredDevotional.category] }}
                  >
                    {featuredDevotional.category}
                  </span>
                  <span className="flex items-center gap-1 text-white/70 text-xs font-satoshi">
                    <Clock size={12} />
                    {featuredDevotional.duration}
                  </span>
                </div>
                
                <h2 className="font-instrument text-2xl text-white mb-2">
                  {featuredDevotional.title}
                </h2>
                
                <p className="font-instrument italic text-white/80 text-sm line-clamp-2">
                  "{featuredDevotional.powr_principle}"
                </p>
              </div>

              {/* Play Button */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <Play size={28} weight="fill" className="text-white ml-1" />
                </div>
              </div>

              {/* Completion Badge */}
              {featuredDevotional.is_completed && (
                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#B8932B] flex items-center justify-center">
                  <Check size={18} weight="bold" className="text-white" />
                </div>
              )}
            </motion.div>
          </Link>
        </section>
      )}

      {/* Filter Chips */}
      <section className="px-5 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {['all', 'old', 'new'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`category-pill whitespace-nowrap ${
                filter === f ? 'category-pill-active' : 'category-pill-inactive'
              }`}
            >
              {f === 'all' ? 'All' : f === 'old' ? 'Old Testament' : 'New Testament'}
            </button>
          ))}
        </div>
        
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto mt-3 pb-2 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}
              className={`category-pill whitespace-nowrap ${
                categoryFilter === cat ? 'category-pill-active' : 'category-pill-inactive'
              }`}
              style={categoryFilter === cat ? { backgroundColor: CATEGORY_COLORS[cat] } : {}}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Devotionals Grid */}
      <section className="px-5 py-4">
        <div className="grid grid-cols-2 gap-4">
          {devotionals.slice(1).map((devotional, idx) => (
            <Link key={devotional.id} to={`/devotional/${devotional.id}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="relative rounded-2xl overflow-hidden aspect-[3/4] group"
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ 
                    backgroundImage: `url(${DEVOTIONAL_IMAGES[devotional.character_image] || DEVOTIONAL_IMAGES.david})`,
                    backgroundColor: '#2A2724'
                  }}
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[devotional.category] }}
                    />
                    <span className="text-[10px] text-white/70 font-satoshi uppercase tracking-wider">
                      {devotional.category}
                    </span>
                  </div>
                  
                  <h3 className="font-instrument text-base text-white leading-tight mb-1">
                    {devotional.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-white/60 text-xs font-satoshi">
                    <Clock size={10} />
                    <span>{devotional.duration}</span>
                  </div>
                </div>

                {/* Completion Badge */}
                {devotional.is_completed && (
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#B8932B] flex items-center justify-center">
                    <Check size={14} weight="bold" className="text-white" />
                  </div>
                )}
              </motion.div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1A1714]/10 z-50">
        <div className="flex items-center justify-around py-3">
          <Link 
            to="/devotionals" 
            className="flex flex-col items-center gap-1 text-[#B8932B]"
          >
            <Compass size={24} weight="fill" />
            <span className="text-xs font-satoshi font-medium">Devotionals</span>
          </Link>
          
          <Link 
            to="/devotionals/leaders" 
            className="flex flex-col items-center gap-1 text-[#6B6560] hover:text-[#1A1714] transition-colors"
          >
            <Crown size={24} />
            <span className="text-xs font-satoshi">Leaders</span>
          </Link>
          
          <Link 
            to="/devotionals/growth" 
            className="flex flex-col items-center gap-1 text-[#6B6560] hover:text-[#1A1714] transition-colors"
          >
            <Trophy size={24} />
            <span className="text-xs font-satoshi">Growth</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default DevotionalsPage;
